/**
 * Visitor Tracking Utility
 * 
 * Tracks page views on the public website by sending data to the CRM visitor tracking API.
 * Fails silently to avoid disrupting user experience.
 */

// Public API endpoint for visitor tracking (direct API Gateway, not CloudFront)
const TRACK_API = 'https://gyyakwn6qc.execute-api.eu-south-2.amazonaws.com/dev/trackVisitor';

/**
 * Track a page view
 * 
 * @param {string} pageUrl - The current page URL
 * @param {string} pageTitle - The page title
 * @returns {Promise<void>}
 */
export async function trackPageView(pageUrl, pageTitle) {
  // Skip tracking in localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('[Visitor Tracking] Skipped (localhost)');
    return;
  }

  try {
    console.log('[Visitor Tracking]', { pageUrl, pageTitle });

    const response = await fetch(TRACK_API, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        url: pageUrl,
        title: pageTitle,
        referrer: document.referrer || '',
        timestamp: new Date().toISOString()
      })
    });

    if (!response.ok) {
      console.warn('Visitor tracking response not OK:', response.status);
      return;
    }

    // Store visitor ID from response
    const data = await response.json();
    if (data.success && data.visitorId) {
      setVisitorId(data.visitorId);
    }
  } catch (error) {
    // Fail silently - don't disrupt user experience
    console.error('Visitor tracking failed:', error);
  }
}

/**
 * Get the current visitor ID from session storage (if available)
 * This is set by the tracking API response
 * 
 * @returns {string|null}
 */
export function getVisitorId() {
  try {
    return sessionStorage.getItem('bluecmr_visitor_id');
  } catch (error) {
    return null;
  }
}

/**
 * Set the visitor ID in session storage
 * 
 * @param {string} visitorId
 */
export function setVisitorId(visitorId) {
  try {
    sessionStorage.setItem('bluecmr_visitor_id', visitorId);
  } catch (error) {
    console.error('Failed to set visitor ID:', error);
  }
}
